import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import AdminLayout from './AdminLayout';
import {
  Search, CheckCircle, XCircle,
  ExternalLink, Eye, FileText, Globe,
  Mail, Building2, RefreshCw, Ban
} from 'lucide-react';

const STATUS_TABS = [
  { key: 'all', label: 'All' },
  { key: 'pending', label: 'Pending' },
  { key: 'under_review', label: 'Under Review' },
  { key: 'verified', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
];

const STATUS_CONFIG = {
  pending: { color: '#F59E0B', bg: '#FEF3C7', label: 'Pending Review' },
  under_review: { color: '#3B82F6', bg: '#DBEAFE', label: 'Under Review' },
  verified: { color: '#10B981', bg: '#D1FAE5', label: 'Approved' },
  rejected: { color: '#EF4444', bg: '#FFE4E6', label: 'Rejected' },
};

export default function AdminCompanies() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [activeTab, setActiveTab] = useState('pending');
  const [search, setSearch] = useState('');
  const [processing, setProcessing] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [actionMsg, setActionMsg] = useState(null);
  const [banModal, setBanModal] = useState(null);
  const [banForm, setBanForm] = useState({ type: 'temp', reason: '', days: 7 });
  const [banProcessing, setBanProcessing] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await supabase
        .from('companies')
        .select('*')
        .eq('account_deleted', false)
        .order('created_at', { ascending: false });
      setCompanies(data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const banCompany = async () => {
    if (!banModal || !banForm.reason.trim()) return;
    setBanProcessing(true);
    try {
      const banUntil = banForm.type === 'temp'
        ? new Date(Date.now() + banForm.days * 24 * 60 * 60 * 1000).toISOString()
        : null;
      await supabase.from('companies').update({
        is_banned: true,
        ban_type: banForm.type,
        ban_reason: banForm.reason,
        ban_until: banUntil,
        ban_issued_at: new Date().toISOString(),
      }).eq('id', banModal.id);
      // Update local state too
      setCompanies(prev => prev.map(c =>
        c.id === banModal.id
          ? { ...c, is_banned: true, ban_type: banForm.type,
              ban_reason: banForm.reason, ban_until: banUntil }
          : c
      ));
      if (selected?.id === banModal.id) {
        setSelected(prev => ({
          ...prev, is_banned: true, ban_type: banForm.type,
          ban_reason: banForm.reason, ban_until: banUntil,
        }));
      }
      setBanModal(null);
      setBanForm({ type: 'temp', reason: '', days: 7 });
      setActionMsg({ type: 'error', text: `🚫 ${banModal.name} has been banned.` });
      setTimeout(() => setActionMsg(null), 4000);
    } catch (err) {
      console.error(err);
      setActionMsg({ type: 'error', text: err.message });
    } finally {
      setBanProcessing(false);
    }
  };

  const unbanCompany = async (companyId) => {
    try {
      await supabase.from('companies').update({
        is_banned: false,
        ban_type: null,
        ban_reason: null,
        ban_until: null,
      }).eq('id', companyId);
      setCompanies(prev => prev.map(c =>
        c.id === companyId
          ? { ...c, is_banned: false, ban_type: null, ban_reason: null, ban_until: null }
          : c
      ));
      if (selected?.id === companyId) {
        setSelected(prev => ({
          ...prev, is_banned: false, ban_type: null,
          ban_reason: null, ban_until: null,
        }));
      }
      setActionMsg({ type: 'success', text: '✅ Company ban has been lifted.' });
      setTimeout(() => setActionMsg(null), 4000);
    } catch (err) {
      console.error(err);
    }
  };

  const filtered = companies.filter(c => {
    if (activeTab !== 'all' && c.verification_status !== activeTab) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        c.name?.toLowerCase().includes(q) ||
        c.contact_email?.toLowerCase().includes(q) ||
        c.country?.toLowerCase().includes(q) ||
        c.business_reg_number?.toLowerCase().includes(q)
      );
    }
    return true;
  });

  const counts = STATUS_TABS.reduce((acc, t) => {
    acc[t.key] = t.key === 'all'
      ? companies.length
      : companies.filter(c => c.verification_status === t.key).length;
    return acc;
  }, {});

  const updateStatus = async (companyId, status, extra = {}) => {
    setProcessing(true);
    setActionMsg(null);
    try {
      const updates = {
        verification_status: status,
        is_verified: status === 'verified',
        admin_notes: adminNotes || null,
        ...extra,
      };
      if (status === 'verified') {
        updates.approved_at = new Date().toISOString();
        updates.approved_by = 'UNIVERSE Admin';
        updates.rejected_at = null;
        updates.rejection_reason = null;
      }
      if (status === 'rejected') {
        updates.rejected_at = new Date().toISOString();
        updates.rejection_reason = rejectReason || null;
        updates.approved_at = null;
      }
      if (status === 'under_review') {
        updates.approved_at = null;
      }
      const { error } = await supabase
        .from('companies').update(updates).eq('id', companyId);
      if (error) throw error;
      setCompanies(prev => prev.map(c =>
        c.id === companyId ? { ...c, ...updates } : c
      ));
      if (selected?.id === companyId) {
        setSelected(prev => ({ ...prev, ...updates }));
      }
      setRejectReason('');
      setAdminNotes('');
      setShowRejectForm(false);
      const msgs = {
        verified: '✅ Company approved! They can now post jobs.',
        rejected: '❌ Company rejected. They have been notified.',
        under_review: '🔍 Marked as under review.',
        pending: '⏳ Moved back to pending.',
      };
      setActionMsg({
        type: status === 'verified' ? 'success'
          : status === 'rejected' ? 'error' : 'info',
        text: msgs[status] || 'Status updated.',
      });
      setTimeout(() => setActionMsg(null), 4000);
    } catch (err) {
      setActionMsg({ type: 'error', text: err.message });
    } finally {
      setProcessing(false);
    }
  };

  return (
    <AdminLayout>
      <div style={{ display: 'flex', gap: 20, height: 'calc(100vh - 130px)' }}>

        {/* ── Left Panel ── */}
        <div style={{
          width: 360, flexShrink: 0,
          display: 'flex', flexDirection: 'column', gap: 12,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h1 style={{ fontSize: 20, fontWeight: 800, color: '#1E293B' }}>Companies</h1>
            <button onClick={load} style={{
              background: '#F8FAFF', border: '1px solid #E2E8F0',
              borderRadius: 8, padding: '6px 10px', cursor: 'pointer',
              color: '#64748B', display: 'flex', alignItems: 'center',
              gap: 4, fontSize: 12, fontWeight: 600,
            }}>
              <RefreshCw size={13} />Refresh
            </button>
          </div>

          <div style={{ position: 'relative' }}>
            <Search size={14} style={{
              position: 'absolute', left: 10,
              top: '50%', transform: 'translateY(-50%)', color: '#94A3B8',
            }} />
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search by name, email, country..."
              style={{
                width: '100%', padding: '9px 12px 9px 30px',
                border: '1.5px solid #E2E8F0', borderRadius: 10,
                fontSize: 13, background: '#fff', outline: 'none',
              }} />
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {STATUS_TABS.map(t => {
              const sc = STATUS_CONFIG[t.key] || { color: '#4F8EF7', bg: '#EEF2FF' };
              const active = activeTab === t.key;
              return (
                <button key={t.key} onClick={() => setActiveTab(t.key)} style={{
                  padding: '5px 10px', borderRadius: 6,
                  border: active
                    ? `1.5px solid ${t.key === 'all' ? '#4F8EF7' : sc.color}`
                    : '1.5px solid #E2E8F0',
                  background: active ? (t.key === 'all' ? '#EEF2FF' : sc.bg) : '#fff',
                  color: active ? (t.key === 'all' ? '#4F8EF7' : sc.color) : '#64748B',
                  fontSize: 11, fontWeight: 600, cursor: 'pointer',
                }}>
                  {t.label} ({counts[t.key]})
                </button>
              );
            })}
          </div>

          {actionMsg && (
            <div style={{
              padding: '10px 14px', borderRadius: 10,
              fontSize: 12, fontWeight: 600,
              background: actionMsg.type === 'success' ? '#F0FDF4'
                : actionMsg.type === 'error' ? '#FFF1F2' : '#EFF6FF',
              color: actionMsg.type === 'success' ? '#16A34A'
                : actionMsg.type === 'error' ? '#E11D48' : '#1D4ED8',
              border: `1px solid ${actionMsg.type === 'success' ? '#BBF7D0'
                : actionMsg.type === 'error' ? '#FECDD3' : '#BFDBFE'}`,
            }}>
              {actionMsg.text}
            </div>
          )}

          {/* Company list */}
          <div style={{
            flex: 1, overflowY: 'auto',
            display: 'flex', flexDirection: 'column', gap: 8,
          }}>
            {loading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
                <div className="spinner" />
              </div>
            ) : filtered.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#94A3B8', fontSize: 13 }}>
                No companies found
              </div>
            ) : filtered.map(c => {
              const sc = STATUS_CONFIG[c.verification_status] || STATUS_CONFIG.pending;
              const isSelected = selected?.id === c.id;
              return (
                <div key={c.id}
                  onClick={() => {
                    setSelected(c);
                    setShowRejectForm(false);
                    setAdminNotes(c.admin_notes || '');
                  }}
                  style={{
                    background: isSelected
                      ? 'linear-gradient(135deg,rgba(79,142,247,0.07),rgba(124,58,237,0.04))'
                      : '#fff',
                    borderRadius: 12, padding: 14,
                    border: isSelected
                      ? '1.5px solid rgba(79,142,247,0.35)'
                      : c.is_banned
                        ? '1.5px solid rgba(239,68,68,0.3)'
                        : '1.5px solid #E2E8F0',
                    cursor: 'pointer', transition: 'all 0.15s',
                  }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    <div style={{
                      width: 40, height: 40, borderRadius: 10,
                      background: c.is_banned
                        ? '#EF4444'
                        : 'linear-gradient(135deg,#4F8EF7,#7C3AED)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: '#fff', fontWeight: 800, fontSize: 15, flexShrink: 0,
                    }}>
                      {c.is_banned ? '🚫' : (c.name?.[0]?.toUpperCase() || 'C')}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontWeight: 700, fontSize: 13, color: '#1E293B',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>{c.name}</div>
                      <div style={{
                        fontSize: 11, color: '#94A3B8',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>{c.contact_email}</div>
                      <div style={{ fontSize: 10, color: '#94A3B8', marginTop: 2 }}>
                        {c.industry} · {c.city}, {c.country}
                      </div>
                      {c.is_banned && (
                        <div style={{ fontSize: 10, color: '#EF4444', fontWeight: 700, marginTop: 2 }}>
                          🚫 {c.ban_type === 'permanent' ? 'Permanently Banned'
                            : `Banned until ${new Date(c.ban_until).toLocaleDateString()}`}
                        </div>
                      )}
                    </div>
                    <span style={{
                      fontSize: 9, fontWeight: 800, padding: '3px 7px',
                      borderRadius: 4, background: sc.bg, color: sc.color,
                      flexShrink: 0, letterSpacing: 0.3,
                    }}>{sc.label.toUpperCase()}</span>
                  </div>
                  <div style={{ fontSize: 10, color: '#94A3B8', marginTop: 6 }}>
                    Registered {new Date(c.created_at).toLocaleDateString('en-US', {
                      month: 'short', day: 'numeric', year: 'numeric',
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Right Panel ── */}
        <div style={{
          flex: 1, overflowY: 'auto',
          background: '#fff', borderRadius: 18,
          border: '1px solid #E2E8F0',
        }}>
          {!selected ? (
            <div style={{
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
              height: '100%', color: '#94A3B8', gap: 12,
            }}>
              <Building2 size={48} strokeWidth={1.5} />
              <div style={{ fontSize: 15, fontWeight: 600, color: '#64748B' }}>
                Select a company to review
              </div>
            </div>
          ) : (
            <CompanyDetail
              company={selected}
              processing={processing}
              rejectReason={rejectReason}
              setRejectReason={setRejectReason}
              adminNotes={adminNotes}
              setAdminNotes={setAdminNotes}
              showRejectForm={showRejectForm}
              setShowRejectForm={setShowRejectForm}
              onUpdateStatus={updateStatus}
              onBan={() => setBanModal(selected)}
              onUnban={() => unbanCompany(selected.id)}
            />
          )}
        </div>
      </div>

      {/* ── Ban Modal ── */}
      {banModal && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1000,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center',
          justifyContent: 'center', padding: 20,
        }}>
          <div style={{
            background: '#fff', borderRadius: 20, padding: 32,
            maxWidth: 460, width: '100%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          }}>
            {/* Modal header */}
            <div style={{
              display: 'flex', justifyContent: 'space-between',
              alignItems: 'center', marginBottom: 20,
            }}>
              <div>
                <h2 style={{ fontSize: 18, fontWeight: 800, color: '#1E293B', margin: 0 }}>
                  Ban Company
                </h2>
                <p style={{ fontSize: 13, color: '#94A3B8', margin: '4px 0 0' }}>
                  {banModal.name}
                </p>
              </div>
              <button onClick={() => { setBanModal(null); setBanForm({ type: 'temp', reason: '', days: 7 }); }}
                style={{
                  border: 'none', background: '#F1F5F9',
                  borderRadius: 8, width: 32, height: 32,
                  fontSize: 18, cursor: 'pointer', color: '#94A3B8',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>×</button>
            </div>

            {/* Ban type */}
            <div style={{ marginBottom: 16 }}>
              <label style={{
                display: 'block', fontSize: 12, fontWeight: 700,
                color: '#64748B', marginBottom: 8, letterSpacing: 0.5,
              }}>BAN TYPE</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {[
                  { value: 'temp', label: '⏳ Temporary', desc: 'Lifted after set days' },
                  { value: 'permanent', label: '🚫 Permanent', desc: 'Never auto-lifted' },
                ].map(opt => (
                  <button key={opt.value}
                    onClick={() => setBanForm(f => ({ ...f, type: opt.value }))}
                    style={{
                      flex: 1, padding: '12px 10px', borderRadius: 10,
                      border: banForm.type === opt.value
                        ? '2px solid #EF4444' : '1.5px solid #E2E8F0',
                      background: banForm.type === opt.value ? '#FFF1F2' : '#fff',
                      color: banForm.type === opt.value ? '#EF4444' : '#64748B',
                      fontSize: 13, fontWeight: 700, cursor: 'pointer',
                      textAlign: 'center',
                    }}>
                    <div>{opt.label}</div>
                    <div style={{ fontSize: 10, fontWeight: 400, marginTop: 3, opacity: 0.7 }}>
                      {opt.desc}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Duration (temp only) */}
            {banForm.type === 'temp' && (
              <div style={{ marginBottom: 16 }}>
                <label style={{
                  display: 'block', fontSize: 12, fontWeight: 700,
                  color: '#64748B', marginBottom: 8, letterSpacing: 0.5,
                }}>DURATION</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {[7, 14, 30, 60, 90].map(d => (
                    <button key={d}
                      onClick={() => setBanForm(f => ({ ...f, days: d }))}
                      style={{
                        flex: 1, padding: '10px 0', borderRadius: 8,
                        border: banForm.days === d
                          ? '2px solid #EF4444' : '1.5px solid #E2E8F0',
                        background: banForm.days === d ? '#FFF1F2' : '#fff',
                        color: banForm.days === d ? '#EF4444' : '#64748B',
                        fontSize: 13, fontWeight: 700, cursor: 'pointer',
                      }}>
                      {d}d
                    </button>
                  ))}
                </div>
                {banForm.type === 'temp' && (
                  <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 6 }}>
                    Ban expires: {new Date(Date.now() + banForm.days * 24 * 60 * 60 * 1000)
                      .toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                  </div>
                )}
              </div>
            )}

            {/* Reason */}
            <div style={{ marginBottom: 20 }}>
              <label style={{
                display: 'block', fontSize: 12, fontWeight: 700,
                color: '#64748B', marginBottom: 8, letterSpacing: 0.5,
              }}>REASON *</label>
              <textarea
                value={banForm.reason}
                onChange={e => setBanForm(f => ({ ...f, reason: e.target.value }))}
                placeholder="Explain why this company is being banned. This will be recorded in the system."
                rows={3}
                style={{
                  width: '100%', padding: '12px 14px',
                  border: '1.5px solid #E2E8F0', borderRadius: 10,
                  fontSize: 13, resize: 'none', outline: 'none',
                  color: '#1E293B', lineHeight: 1.5,
                }} />
            </div>

            {/* Warning */}
            <div style={{
              background: '#FFF7ED', border: '1px solid #FED7AA',
              borderRadius: 10, padding: '12px 14px', marginBottom: 20,
              fontSize: 12, color: '#92400E', lineHeight: 1.5,
            }}>
              ⚡ This will immediately {banForm.type === 'temp'
                ? `suspend <strong>${banModal.name}</strong> for ${banForm.days} days`
                : `permanently ban <strong>${banModal.name}</strong>`}.
              Their job listings will be hidden from students.
            </div>

            {/* Buttons */}
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => { setBanModal(null); setBanForm({ type: 'temp', reason: '', days: 7 }); }}
                style={{
                  flex: 1, padding: '12px 0', borderRadius: 10,
                  border: '1px solid #E2E8F0', background: '#fff',
                  color: '#64748B', fontWeight: 600, cursor: 'pointer', fontSize: 14,
                }}>Cancel</button>
              <button
                onClick={banCompany}
                disabled={banProcessing || !banForm.reason.trim()}
                style={{
                  flex: 2, padding: '12px 0', borderRadius: 10,
                  border: 'none', background: '#EF4444',
                  color: '#fff', fontWeight: 700, cursor: 'pointer',
                  fontSize: 14,
                  opacity: banProcessing || !banForm.reason.trim() ? 0.5 : 1,
                }}>
                {banProcessing
                  ? 'Processing...'
                  : `Confirm ${banForm.type === 'temp'
                      ? `${banForm.days}-Day Ban`
                      : 'Permanent Ban'}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

// ── Company Detail ────────────────────────────────────
function CompanyDetail({
  company: c, processing, rejectReason,
  setRejectReason, adminNotes, setAdminNotes,
  showRejectForm, setShowRejectForm, onUpdateStatus,
  onBan, onUnban,
}) {
  const sc = STATUS_CONFIG[c.verification_status] || STATUS_CONFIG.pending;
  const canApprove = ['pending', 'under_review'].includes(c.verification_status);
  const canReject = ['pending', 'under_review'].includes(c.verification_status);
  const canMarkReview = c.verification_status === 'pending';

  return (
    <div style={{ padding: 28 }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'flex-start', gap: 16,
        marginBottom: 24, paddingBottom: 20,
        borderBottom: '1px solid #F1F5F9',
      }}>
        <div style={{
          width: 64, height: 64, borderRadius: 16,
          background: c.logo_url ? 'transparent' : 'linear-gradient(135deg,#4F8EF7,#7C3AED)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#fff', fontWeight: 900, fontSize: 24,
          flexShrink: 0, overflow: 'hidden', border: '1px solid #E2E8F0',
        }}>
          {c.logo_url ? (
            <img src={c.logo_url} alt={c.name}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : c.name?.[0]?.toUpperCase()}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <h2 style={{ fontSize: 20, fontWeight: 800, color: '#1E293B' }}>{c.name}</h2>
            <span style={{
              fontSize: 11, fontWeight: 700, padding: '4px 10px',
              borderRadius: 6, background: sc.bg, color: sc.color,
            }}>{sc.label}</span>
            {c.is_banned && (
              <span style={{
                fontSize: 11, fontWeight: 700, padding: '4px 10px',
                borderRadius: 6, background: '#FFE4E6', color: '#EF4444',
              }}>
                🚫 {c.ban_type === 'permanent' ? 'PERMANENTLY BANNED'
                  : `BANNED UNTIL ${new Date(c.ban_until).toLocaleDateString()}`}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 4 }}>
            {[
              c.industry, c.company_type,
              c.company_size && `${c.company_size} employees`,
              c.founded_year && `Est. ${c.founded_year}`,
            ].filter(Boolean).map((t, i) => (
              <span key={i} style={{ fontSize: 12, color: '#94A3B8' }}>{t}</span>
            ))}
          </div>
        </div>
      </div>

      {/* ── Action Buttons ── */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
        {canMarkReview && (
          <ActionBtn
            label="Mark Under Review"
            icon={<Eye size={14} />}
            color="#3B82F6"
            onClick={() => onUpdateStatus(c.id, 'under_review')}
            loading={processing} />
        )}
        {canApprove && (
          <ActionBtn
            label="✅ Approve Company"
            icon={<CheckCircle size={14} />}
            color="#10B981"
            onClick={() => onUpdateStatus(c.id, 'verified')}
            loading={processing}
            primary />
        )}
        {canReject && (
          <ActionBtn
            label="❌ Reject"
            icon={<XCircle size={14} />}
            color="#EF4444"
            onClick={() => setShowRejectForm(!showRejectForm)}
            loading={false}
            outline />
        )}
        {c.verification_status === 'verified' && (
          <ActionBtn
            label="Revoke Approval"
            icon={<XCircle size={14} />}
            color="#F59E0B"
            onClick={() => onUpdateStatus(c.id, 'pending')}
            loading={processing}
            outline />
        )}

        {/* ── BAN / UNBAN BUTTON ── */}
        {c.is_banned ? (
          <ActionBtn
            label="✅ Lift Ban"
            icon={<CheckCircle size={14} />}
            color="#10B981"
            onClick={onUnban}
            loading={processing}
            primary />
        ) : (
          <ActionBtn
            label="🚫 Ban Company"
            icon={<Ban size={14} />}
            color="#EF4444"
            onClick={onBan}
            loading={false}
            outline />
        )}
      </div>

      {/* Ban info banner (if banned) */}
      {c.is_banned && (
        <div style={{
          background: '#FFF1F2', border: '1.5px solid #FECDD3',
          borderRadius: 12, padding: '14px 18px', marginBottom: 20,
          display: 'flex', alignItems: 'flex-start', gap: 12,
        }}>
          <span style={{ fontSize: 22 }}>🚫</span>
          <div>
            <div style={{ fontWeight: 700, color: '#9F1239', fontSize: 14, marginBottom: 4 }}>
              Company is {c.ban_type === 'permanent' ? 'permanently' : 'temporarily'} banned
            </div>
            <div style={{ fontSize: 13, color: '#BE123C', lineHeight: 1.5 }}>
              <strong>Reason:</strong> {c.ban_reason}
            </div>
            {c.ban_until && (
              <div style={{ fontSize: 12, color: '#94A3B8', marginTop: 4 }}>
                Expires: {new Date(c.ban_until).toLocaleString('en-US', {
                  month: 'long', day: 'numeric', year: 'numeric',
                  hour: '2-digit', minute: '2-digit',
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Reject form */}
      {showRejectForm && (
        <div style={{
          background: '#FFF1F2', border: '1px solid #FECDD3',
          borderRadius: 14, padding: 18, marginBottom: 20,
        }}>
          <div style={{ fontWeight: 700, color: '#9F1239', marginBottom: 10, fontSize: 14 }}>
            Rejection Reason
          </div>
          <textarea
            value={rejectReason}
            onChange={e => setRejectReason(e.target.value)}
            placeholder="Explain why this company is being rejected. This will be shown to the company on their dashboard..."
            rows={4}
            style={{
              width: '100%', padding: '10px 12px',
              border: '1px solid #FECDD3', borderRadius: 10,
              fontSize: 13, resize: 'vertical', marginBottom: 12, outline: 'none',
            }} />
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => onUpdateStatus(c.id, 'rejected', { rejection_reason: rejectReason })}
              disabled={processing || !rejectReason.trim()}
              style={{
                padding: '9px 18px',
                background: rejectReason.trim() ? '#EF4444' : '#CBD5E1',
                border: 'none', borderRadius: 8, color: '#fff',
                fontWeight: 700, fontSize: 12,
                cursor: rejectReason.trim() ? 'pointer' : 'not-allowed',
              }}>
              Confirm Rejection
            </button>
            <button onClick={() => setShowRejectForm(false)} style={{
              padding: '9px 14px', background: '#fff',
              border: '1px solid #E2E8F0', borderRadius: 8,
              color: '#64748B', fontWeight: 600, fontSize: 12, cursor: 'pointer',
            }}>Cancel</button>
          </div>
        </div>
      )}

      {/* Admin notes */}
      <div style={{ marginBottom: 20 }}>
        <label style={{
          display: 'block', fontSize: 12, fontWeight: 600,
          color: '#475569', marginBottom: 6,
        }}>Internal Admin Notes (not shown to company)</label>
        <textarea
          value={adminNotes}
          onChange={e => setAdminNotes(e.target.value)}
          placeholder="Internal notes about this company..."
          rows={2}
          style={{
            width: '100%', padding: '10px 12px',
            border: '1.5px solid #E2E8F0', borderRadius: 10,
            fontSize: 13, resize: 'vertical', outline: 'none',
            background: '#F8FAFF',
          }} />
      </div>

      {/* Info sections */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 20 }}>
        <InfoSection title="Contact Information" icon={<Mail size={14} />}>
          <InfoItem label="Contact Person" value={c.contact_name} />
          <InfoItem label="Email" value={c.contact_email} />
          <InfoItem label="Phone" value={c.contact_phone} />
        </InfoSection>
        <InfoSection title="Location" icon={<Building2 size={14} />}>
          <InfoItem label="City" value={c.city} />
          <InfoItem label="Country" value={c.country} />
          <InfoItem label="Address" value={c.address || 'Not provided'} />
        </InfoSection>
        <InfoSection title="Legal Information" icon={<FileText size={14} />}>
          <InfoItem label="Business Reg #" value={c.business_reg_number} />
          <InfoItem label="Tax ID / NTN" value={c.tax_id || 'Not provided'} />
          <InfoItem label="Company Type" value={c.company_type} />
        </InfoSection>
        <InfoSection title="Company Details" icon={<Globe size={14} />}>
          <InfoItem label="Industry" value={c.industry} />
          <InfoItem label="Size" value={c.company_size} />
          <InfoItem label="Founded" value={c.founded_year || 'N/A'} />
        </InfoSection>
      </div>

      {/* Description */}
      {c.description && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: '#1E293B', marginBottom: 8 }}>
            Company Description
          </div>
          <div style={{
            background: '#F8FAFF', border: '1px solid #E2E8F0',
            borderRadius: 10, padding: '12px 14px',
            fontSize: 13, color: '#475569', lineHeight: 1.6,
          }}>{c.description}</div>
        </div>
      )}

      {/* Links */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        {c.website && (
          <a href={c.website} target="_blank" rel="noreferrer" style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '7px 12px',
            background: 'rgba(79,142,247,0.1)', border: '1px solid rgba(79,142,247,0.25)',
            borderRadius: 8, color: '#4F8EF7', fontSize: 12, fontWeight: 600,
            textDecoration: 'none',
          }}>
            <Globe size={13} />Website <ExternalLink size={11} />
          </a>
        )}
        {c.linkedin_url && (
          <a href={c.linkedin_url} target="_blank" rel="noreferrer" style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '7px 12px',
            background: 'rgba(0,119,181,0.1)', border: '1px solid rgba(0,119,181,0.25)',
            borderRadius: 8, color: '#0077B5', fontSize: 12, fontWeight: 600,
            textDecoration: 'none',
          }}>
            LinkedIn <ExternalLink size={11} />
          </a>
        )}
        {c.reg_certificate_url && (
          <a href={c.reg_certificate_url} target="_blank" rel="noreferrer"
            onClick={async (e) => {
              e.preventDefault();
              try {
                const urlParts = c.reg_certificate_url.split('/avatars/');
                if (urlParts.length > 1) {
                  const path = decodeURIComponent(urlParts[1].split('?')[0]);
                  const { data: sd } = await supabase.storage
                    .from('avatars').createSignedUrl(path, 3600);
                  window.open(sd?.signedUrl || c.reg_certificate_url, '_blank');
                } else {
                  window.open(c.reg_certificate_url, '_blank');
                }
              } catch (_) {
                window.open(c.reg_certificate_url, '_blank');
              }
            }}
            style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '7px 12px',
              background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.25)',
              borderRadius: 8, color: '#10B981', fontSize: 12, fontWeight: 600,
              textDecoration: 'none',
            }}>
            <FileText size={13} />📄 View Registration Document <ExternalLink size={11} />
          </a>
        )}
      </div>

      {/* Status history */}
      {(c.approved_at || c.rejected_at) && (
        <div style={{
          background: '#F8FAFF', border: '1px solid #E2E8F0',
          borderRadius: 12, padding: 14,
        }}>
          <div style={{ fontWeight: 700, fontSize: 12, color: '#475569', marginBottom: 8 }}>
            Status History
          </div>
          {c.approved_at && (
            <div style={{ fontSize: 12, color: '#10B981', marginBottom: 4 }}>
              ✅ Approved on {new Date(c.approved_at).toLocaleDateString('en-US', {
                month: 'long', day: 'numeric', year: 'numeric',
              })}
              {c.approved_by && ` by ${c.approved_by}`}
            </div>
          )}
          {c.rejected_at && (
            <div style={{ fontSize: 12, color: '#EF4444' }}>
              ❌ Rejected on {new Date(c.rejected_at).toLocaleDateString('en-US', {
                month: 'long', day: 'numeric', year: 'numeric',
              })}
            </div>
          )}
          {c.rejection_reason && (
            <div style={{ fontSize: 12, color: '#94A3B8', marginTop: 4, fontStyle: 'italic' }}>
              Reason: {c.rejection_reason}
            </div>
          )}
          {c.admin_notes && (
            <div style={{
              fontSize: 12, color: '#94A3B8', marginTop: 4,
              paddingTop: 8, borderTop: '1px solid #E2E8F0',
            }}>
              📝 Notes: {c.admin_notes}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Reusable components ───────────────────────────────
function ActionBtn({ label, icon, color, onClick, loading, primary = false, outline = false }) {
  const getBg = () => {
    if (loading) return '#CBD5E1';
    if (outline) return `${color}10`;
    if (primary) return `linear-gradient(135deg,${color},${color}dd)`;
    return `${color}15`;
  };
  return (
    <button onClick={onClick} disabled={loading} style={{
      display: 'flex', alignItems: 'center', gap: 6,
      padding: '9px 16px', borderRadius: 9,
      border: outline ? `1.5px solid ${color}` : 'none',
      background: getBg(),
      color: loading ? '#94A3B8' : primary ? '#fff' : color,
      fontSize: 12, fontWeight: 700,
      cursor: loading ? 'not-allowed' : 'pointer',
      opacity: loading ? 0.6 : 1,
      boxShadow: primary && !loading ? `0 4px 12px ${color}30` : 'none',
    }}>
      {loading ? (
        <div style={{
          width: 12, height: 12,
          border: '2px solid rgba(0,0,0,0.1)',
          borderTopColor: color, borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
        }} />
      ) : icon}
      {label}
    </button>
  );
}

function InfoSection({ title, icon, children }) {
  return (
    <div style={{
      background: '#F8FAFF', border: '1px solid #E2E8F0',
      borderRadius: 12, padding: 14,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        fontWeight: 700, fontSize: 12, color: '#475569', marginBottom: 10,
      }}>
        {icon}{title}
      </div>
      {children}
    </div>
  );
}

function InfoItem({ label, value }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between',
      fontSize: 12, padding: '4px 0', borderBottom: '1px solid #F1F5F9',
    }}>
      <span style={{ color: '#94A3B8' }}>{label}</span>
      <span style={{
        color: '#1E293B', fontWeight: 600, maxWidth: 160, textAlign: 'right',
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>{value || '—'}</span>
    </div>
  );
}